import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, eaAPI, emailInboxAPI, suppliersAPI, aiKeyAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Briefcase, Users, FileText, DollarSign, 
  TrendingUp, Clock, AlertCircle, Building2, ShoppingCart, Calendar,
  ListChecks, ArrowRight, Sparkles, CheckCircle2, MailSearch,
  KeyRound, Circle, X
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ActionListWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { eaAPI.actionList().then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const top = (data.items || []).slice(0, 3);
  const total = data.counts?.total || 0;
  return (
    <Card className="surface border-primary/20" data-testid="dashboard-action-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl brand-gradient flex items-center justify-center shadow-glow">
              <ListChecks className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold flex items-center gap-1.5">
                Needs you
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 flex items-center gap-1">
                  <Sparkles className="h-2.5 w-2.5" /> AI
                </span>
              </h2>
              <p className="text-xs text-muted-foreground">{total > 0 ? `${total} thing${total === 1 ? '' : 's'} need your attention` : 'You are all caught up'}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/actions')} data-testid="dashboard-view-actions">
            View all <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
        {total === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-400 py-2">
            <CheckCircle2 className="h-4 w-4" /> Nothing needs you — the assistant has it handled.
          </div>
        ) : (
          <div className="space-y-2">
            {top.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(item.link)}
                className="w-full text-left flex items-center gap-3 p-2.5 rounded-lg border border-border hover:border-primary/40 hover:bg-secondary/30 transition-colors"
                data-testid={`dashboard-action-${item.type}`}
              >
                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${item.priority === 'high' ? 'bg-red-500' : item.priority === 'medium' ? 'bg-amber-400' : 'bg-cyan-400'}`} />
                <span className="text-sm truncate flex-1">{item.title}</span>
                <span className="text-xs text-primary flex-shrink-0">{item.cta}</span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('onboarding_dismissed') === '1');
  const [state, setState] = useState({ inbox: false, aiKey: false, suppliers: false });

  useEffect(() => {
    Promise.allSettled([
      emailInboxAPI.getAccount(),
      aiKeyAPI.getStatus(),
      suppliersAPI.getAll(),
    ]).then(([inboxRes, aiRes, supRes]) => {
      setState({
        inbox: inboxRes.status === 'fulfilled' && !!(inboxRes.value?.data && inboxRes.value.data.email_address),
        aiKey: aiRes.status === 'fulfilled' && !!aiRes.value?.data?.configured,
        suppliers: supRes.status === 'fulfilled' && Array.isArray(supRes.value?.data) && supRes.value.data.length > 0,
      });
      setChecked(true);
    });
  }, []);

  if (!checked || dismissed) return null;

  const steps = [
    { key: 'inbox', done: state.inbox, icon: MailSearch, title: 'Connect your inbox',
      desc: 'Link Gmail/IMAP so the assistant auto-imports incoming purchase orders.',
      cta: 'Connect inbox', to: '/settings' },
    { key: 'aiKey', done: state.aiKey, icon: KeyRound, title: 'Add your AI key (optional)',
      desc: 'Use your own OpenAI key for faster, dedicated AI — or leave it to run on shared credits.',
      cta: 'Add AI key', to: '/settings' },
    { key: 'suppliers', done: state.suppliers, icon: Building2, title: 'Add your suppliers',
      desc: 'Add the builders/insurers that send you POs so sender-based capture is bulletproof.',
      cta: 'Add suppliers', to: '/suppliers' },
  ];

  const completed = steps.filter((s) => s.done).length;
  if (completed === steps.length) return null;

  const dismiss = () => { localStorage.setItem('onboarding_dismissed', '1'); setDismissed(true); };

  return (
    <Card className="surface border-primary/20" data-testid="onboarding-checklist">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl brand-gradient flex items-center justify-center shadow-glow">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold">Get set up in 3 steps</h2>
              <p className="text-xs text-muted-foreground">{completed} of {steps.length} done — finish setup to unlock the full assistant</p>
            </div>
          </div>
          <button onClick={dismiss} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="onboarding-dismiss" aria-label="Dismiss setup">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="h-1.5 w-full rounded-full bg-secondary mb-4 overflow-hidden">
          <div className="h-full brand-gradient rounded-full transition-all" style={{ width: `${(completed / steps.length) * 100}%` }} />
        </div>

        <div className="space-y-2">
          {steps.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.key}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${s.done ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border hover:border-primary/40'}`}
                data-testid={`onboarding-step-${s.key}`}
              >
                {s.done
                  ? <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" data-testid={`onboarding-step-${s.key}-done`} />
                  : <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium flex items-center gap-1.5 ${s.done ? 'text-muted-foreground line-through' : ''}`}>
                    <Icon className="h-3.5 w-3.5" /> {s.title}
                  </p>
                  {!s.done && <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>}
                </div>
                {!s.done && (
                  <Button size="sm" variant="outline" className="flex-shrink-0" onClick={() => navigate(s.to)} data-testid={`onboarding-cta-${s.key}`}>
                    {s.cta} <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Active Jobs', 
      value: stats?.active_jobs || 0, 
      total: stats?.total_jobs || 0,
      icon: Briefcase, 
      color: 'text-blue-400',
      bg: 'bg-blue-400/10'
    },
    { 
      title: 'Suppliers', 
      value: stats?.total_suppliers || 0, 
      icon: Building2, 
      color: 'text-green-400',
      bg: 'bg-green-400/10'
    },
    { 
      title: 'Open POs', 
      value: stats?.open_pos || 0, 
      icon: ShoppingCart, 
      color: 'text-yellow-400',
      bg: 'bg-yellow-400/10'
    },
    { 
      title: 'Upcoming Bookings', 
      value: stats?.upcoming_bookings || 0, 
      icon: Calendar, 
      color: 'text-orange-400',
      bg: 'bg-orange-400/10'
    },
    { 
      title: 'Trades', 
      value: stats?.total_contractors || 0, 
      icon: Users, 
      color: 'text-primary',
      bg: 'bg-primary/10'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Connect inbox prompt (hidden once connected / for trade role) */}
      {user?.role !== 'trade' && <OnboardingChecklist />}

      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Welcome back, {user?.name?.split(' ')[0]}</h1>
          <p className="text-muted-foreground mt-1">Here&apos;s what&apos;s happening across your jobs &amp; POs</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          {new Date().toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* AI Action List widget */}
      {user?.role !== 'trade' && <ActionListWidget />}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="surface card-hover overflow-hidden relative" data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className={`h-11 w-11 ${stat.bg} rounded-xl flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                </div>
                <p className="text-3xl font-bold mt-4 font-heading tracking-tight">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.title}</p>
                {stat.total ? (
                  <p className="text-xs text-muted-foreground/70">of {stat.total} total</p>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Rate outlier summary — POs priced above your learned rates this week */}
      {stats?.outlier_po_count > 0 && (
        <button
          type="button"
          onClick={() => navigate('/po-inbox')}
          className="w-full text-left rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-center gap-4 hover:bg-amber-500/15 transition-colors"
          data-testid="outlier-tile"
        >
          <div className="h-11 w-11 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
            <AlertCircle className="h-5 w-5 text-amber-500" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-amber-500">
              This week: {stats.outlier_po_count} PO{stats.outlier_po_count > 1 ? 's' : ''} priced above your rates
            </p>
            <p className="text-sm text-muted-foreground">
              {stats.outlier_item_count} line item{stats.outlier_item_count > 1 ? 's' : ''} averaging <span className="font-medium text-foreground">+{stats.outlier_avg_pct}%</span> over your learned typical — review before accepting
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-amber-500 shrink-0" />
        </button>
      )}

      {/* Recent Activity */}
      <Card className="surface">
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {stats?.recent_activity?.length > 0 ? (
            <div className="divide-y divide-border">
              {stats.recent_activity.map((activity, index) => (
                <div key={activity.id || index} className="flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <div>
                      <p className="text-sm font-medium">
                        <span className="text-muted-foreground">{activity.user_name}</span>
                        {' '}{activity.action}{' '}
                        <span className="text-primary">{activity.entity_type}</span>
                      </p>
                      {activity.details && (
                        <p className="text-xs text-muted-foreground mt-0.5">{activity.details}</p>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No recent activity
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
